import _ from 'lodash';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { getErrorMessage } from '../../../../ReactQuery';
import FallbackImage from '../../../generic/FallbackImage';
import List from '../../../generic/List';
import type { DependencyForAPI } from '../../../../../shared/dependenciesV2';
import type { ManuallyAddedDependencyRequestKind } from '../../../../../typings/api';
import type { DependencyToRemove } from '../../dependenciesV2/modals/RemoveDependency';

import VendoredDependencyRow from './VendoredDependencyRow';

type Props = {
  isLoading: boolean;
  error: Error | null;
  dependencies: DependencyForAPI[];
  setSelectedDependencyRevisionLocator?: (locator: string) => void;
  setDependencyToRemove: (dependency: DependencyToRemove | null) => void;
  setDependencyLocatorToOverwrite: (locator: string | null) => void;
  setManualDependencyAddMode: (mode: ManuallyAddedDependencyRequestKind | null) => void;
  setInProgressBuildId: (buildId: number | null) => void;
};

/**
 * Renders the list of vendored dependencies in a table format.
 * @param {Props} props - The props for the component.
 */
const VendoredDependenciesTable: VoidFunctionComponent<Props> = ({
  isLoading,
  error,
  dependencies,
  setSelectedDependencyRevisionLocator,
  setDependencyToRemove,
  setDependencyLocatorToOverwrite,
  setManualDependencyAddMode,
  setInProgressBuildId,
}) => {
  if (isLoading) {
    return ( // Use of Fragment here to help Typescript
      <>
        {_.times<ReactNode>(10, () => (
          <List.RowLoading />
        ))}
      </>
    );
  }

  if (error) {
    return (
      <FallbackImage src="error-plug" alt="Something went wrong">
        <div>{getErrorMessage(error)}</div>
      </FallbackImage>
    );
  }

  if (dependencies.length === 0) {
    return (
      <FallbackImage src="no-data" alt="Not found">
        No vendored dependencies found.
      </FallbackImage>
    );
  }

  return ( // Use of Fragment here to help Typescript
    <>
      {dependencies.map(dependency => (
        <VendoredDependencyRow
          key={dependency.locator}
          dependency={dependency}
          setSelectedDependencyRevisionLocator={setSelectedDependencyRevisionLocator}
          setDependencyToRemove={setDependencyToRemove}
          setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
          setManualDependencyAddMode={setManualDependencyAddMode}
          setInProgressBuildId={setInProgressBuildId}
        />
      ))}
    </>
  );
};

export default VendoredDependenciesTable;
